import React, { useState } from "react";
import * as Yup from "yup";
import _ from "lodash";
import Modal from "../../../../../components/ModalPopup";
import Button from "../../../../../components/Button";
import Note from "../../../../../components/Note";
import DropDownList from "../../../../../components/DropDownList";
import Label from "../../../../../components/Label";
import { Formik } from "formik";
import { capitalize } from "../../../../../utils/textFunctions";
import styles from "../../RecordTable.module.scss";

import {
  RecordOverrideCountString,
  AttributeDropDownValues,
  ActionDropDownValues,
  IdentifierSystemDropDownValues,
  IdentifierCodeDropDownValues,
  AttrCodeDropDownValues,
  AttributeValueOptions,
  ProposedValueOptions,
} from "../../GoldenRecordExclusion/ComponentValues";
import {
  SelectedPrimaryRecords,
  canShowSummaryTable,
  getRecordsToOverride,
} from "../../GoldenRecordExclusion/Utils";
import SummaryTable from "../../GoldenRecordExclusion/SummaryTable";
import ValidationSchema from "../../GoldenRecordExclusion/ValidationSchema";
import AttributeValues from "../../GoldenRecordExclusion/AttributeValues";

const GoldenRecordExclusionModal = (
  isOpen,
  setIsOpen,
  primaryRecords,
  recordComparisonList,
  attributeValues,
  setAttributeValues,
  setPendingOverrideForm,
  setPendingOverrideValues,
  setIsAuditModalOpen,
  setAuditModalContext,
) => {
  return (
    <Modal open={isOpen} onClose={() => setIsOpen(false)}>
      <div className={styles.bottomBanner}>
        <h2>Override Records</h2>
        <br />
        <h4>
          Only selected primary records will be overridden. <br />
          Choose an attribute to override, action to perform, and value to
          override.
        </h4>
      </div>
      <Formik
        initialValues={{
          override_attribute: "",
          override_action: "",
          override_system: "",
          override_attrCode: "",
          override_correct_value: "",
          override_incorrect_value: "",
          override_code: "",
          selected_records: SelectedPrimaryRecords(
            recordComparisonList,
            primaryRecords,
          ),
        }}
        onSubmit={(formValues) => {
          setPendingOverrideForm(formValues);
          setPendingOverrideValues(attributeValues);
          setAuditModalContext("multi");
          setIsAuditModalOpen(true);
        }}
        validateOnChange={false}
        validationSchema={ValidationSchema(attributeValues)}
      >
        {({ errors, handleChange, handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.bulkOverride}>
              <div className={styles.dropDownDiv}>
                <div className={styles.dropDownDivComponents}>
                  <DropDownList
                    id="override_action"
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("override_system", "");
                      setFieldValue("override_attrCode", "");
                      setFieldValue("override_attribute", "");
                      setFieldValue("override_correct_value", "");
                      setFieldValue("override_incorrect_value", "");
                      setFieldValue("override_code", "");
                    }}
                    errorText={errors.override_action}
                    label={
                      <Label
                        htmlFor="override_action"
                        style={{ fontWeight: "bold" }}
                        error={Boolean(errors.override_action)}
                      >
                        Action
                      </Label>
                    }
                    required
                    value={values.override_action}
                  >
                    <DropDownList.Item value={""}>
                      {"Select Action"}
                    </DropDownList.Item>
                    {ActionDropDownValues.map((v) => (
                      <DropDownList.Item value={v.value}>
                        {capitalize(v.displayName)}
                      </DropDownList.Item>
                    ))}
                  </DropDownList>
                </div>
                <div className={styles.dropDownDivComponents}>
                  <DropDownList
                    id="override_attribute"
                    onChange={(e) => {
                      handleChange(e);
                      setAttributeValues(
                        AttributeValues(
                          e.target.value,
                          values.selected_records,
                        ),
                      );
                      setFieldValue("override_system", "");
                      setFieldValue("override_attrCode", "");
                      setFieldValue("override_correct_value", "");
                      setFieldValue("override_incorrect_value", "");
                      setFieldValue("override_code", "");
                    }}
                    errorText={errors.override_attribute}
                    label={
                      <Label
                        htmlFor="override_attribute"
                        style={{ fontWeight: "bold" }}
                        error={Boolean(errors.override_attribute)}
                      >
                        Attribute
                      </Label>
                    }
                    required
                    value={values.override_attribute}
                  >
                    <DropDownList.Item value={""}>
                      {"Select Attribute"}
                    </DropDownList.Item>
                    {values.override_action !== "" &&
                      AttributeDropDownValues(values.override_action).map(
                        (v) => (
                          <DropDownList.Item value={v.value}>
                            {capitalize(v.displayName)}
                          </DropDownList.Item>
                        ),
                      )}
                  </DropDownList>
                </div>
                {["identifier"].includes(values.override_attribute) && (
                  <div className={styles.dropDownDivComponents}>
                    <DropDownList
                      id="override_system"
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue("override_code", "");
                        setFieldValue("override_correct_value", "");
                        setFieldValue("override_incorrect_value", "");
                      }}
                      errorText={errors.override_system}
                      label={
                        <Label
                          htmlFor="override_system"
                          style={{ fontWeight: "bold" }}
                          error={Boolean(errors.override_system)}
                        >
                          System
                        </Label>
                      }
                      required
                      value={values.override_system}
                    >
                      <DropDownList.Item value={""}>
                        {"Select System"}
                      </DropDownList.Item>
                      {values.override_attribute !== "" &&
                        IdentifierSystemDropDownValues(attributeValues).map(
                          (v) => (
                            <DropDownList.Item value={v}>{v}</DropDownList.Item>
                          ),
                        )}
                    </DropDownList>
                  </div>
                )}
                {["[No System]"].includes(values.override_system) && (
                  <div className={styles.dropDownDivComponents}>
                    <DropDownList
                      id="override_code"
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue("override_correct_value", "");
                        setFieldValue("override_incorrect_value", "");
                      }}
                      errorText={errors.override_code}
                      label={
                        <Label
                          htmlFor="override_code"
                          style={{ fontWeight: "bold" }}
                          error={Boolean(errors.override_code)}
                        >
                          Code
                        </Label>
                      }
                      required
                      value={values.override_code}
                    >
                      <DropDownList.Item value={""}>
                        {"Select Code"}
                      </DropDownList.Item>
                      {values.override_attribute !== "" &&
                        IdentifierCodeDropDownValues(attributeValues).map(
                          (v) => (
                            <DropDownList.Item value={v}>{v}</DropDownList.Item>
                          ),
                        )}
                    </DropDownList>
                  </div>
                )}
                {["phone", "email", "address", "name"].includes(
                  values.override_attribute,
                ) && (
                  <div className={styles.dropDownDivComponents}>
                    <DropDownList
                      id="override_attrCode"
                      onChange={handleChange}
                      errorText={errors.override_attrCode}
                      label={
                        <Label
                          htmlFor="override_attrCode"
                          style={{ fontWeight: "bold" }}
                          error={Boolean(errors.override_attrCode)}
                        >
                          AttrCode
                        </Label>
                      }
                      required
                      value={values.override_attrCode}
                    >
                      <DropDownList.Item value={""}>
                        {"Select AttrCode"}
                      </DropDownList.Item>
                      {values.override_attribute !== "" &&
                        AttrCodeDropDownValues(attributeValues).map((v) => (
                          <DropDownList.Item value={v}>{v}</DropDownList.Item>
                        ))}
                    </DropDownList>
                  </div>
                )}
                <div className={styles.dropDownDivComponents}>
                  <DropDownList
                    className={styles.inputBox}
                    id="override_incorrect_value"
                    onChange={(e) => {
                      handleChange(e);
                      values.override_correct_value = "";
                    }}
                    errorText={errors.override_incorrect_value}
                    label={
                      <Label
                        htmlFor="override_incorrect_value"
                        style={{ fontWeight: "bold" }}
                        error={Boolean(errors.override_incorrect_value)}
                      >
                        Value To Modify
                      </Label>
                    }
                    required
                    value={values.override_incorrect_value}
                    disabled={
                      values.override_attribute.length === 0 ||
                      values.override_action.length === 0 ||
                      (!["birthDate", "gender"].includes(
                        values.override_attribute,
                      ) &&
                        values.override_system.length === 0 &&
                        values.override_attrCode.length === 0) ||
                      (["identifier"].includes(values.override_attribute) &&
                        values.override_system === "[No System]" &&
                        values.override_code === "")
                    }
                  >
                    <DropDownList.Item value={""}>
                      {"Select Value To Override"}
                    </DropDownList.Item>
                    {values.override_attribute !== "" &&
                      AttributeValueOptions(
                        attributeValues,
                        values.override_attribute,
                        values.override_system,
                        values.override_attrCode,
                        values.override_code,
                      ).map((v) => (
                        <DropDownList.Item value={v}>{v}</DropDownList.Item>
                      ))}
                  </DropDownList>
                </div>
                {values.override_action === "replace" && (
                  <div className={styles.dropDownDivComponents}>
                    <DropDownList
                      className={styles.inputBox}
                      id="override_correct_value"
                      onChange={handleChange}
                      errorText={errors.override_correct_value}
                      label={
                        <Label
                          style={{ fontWeight: "bold" }}
                          htmlFor="override_correct_value"
                          error={Boolean(errors.override_correct_value)}
                        >
                          New Value
                        </Label>
                      }
                      required
                      value={values.override_correct_value}
                      disabled={
                        values.override_attribute.length === 0 ||
                        values.override_action !== "replace" ||
                        (!["birthDate", "gender"].includes(
                          values.override_attribute,
                        ) &&
                          values.override_system.length === 0 &&
                          values.override_attrCode.length === 0) ||
                        (["identifier"].includes(values.override_attribute) &&
                          values.override_system === "[No System]" &&
                          values.override_code === "")
                      }
                    >
                      <DropDownList.Item value="">
                        {"Select New Value"}
                      </DropDownList.Item>
                      {values.override_attribute !== "" &&
                        ProposedValueOptions(
                          attributeValues,
                          values.override_attribute,
                          values.override_system,
                          values.override_attrCode,
                          values.override_code,
                        ).map((v) => (
                          <DropDownList.Item value={v}>{v}</DropDownList.Item>
                        ))}
                    </DropDownList>
                  </div>
                )}
              </div>
              <div className={styles.summaryTable}>
                <h3>Override Summary</h3>
                {canShowSummaryTable(values) ? (
                  <SummaryTable
                    attributeMaps={attributeValues}
                    action={values.override_action}
                    attribute={values.override_attribute}
                    proposedValue={values.override_correct_value}
                    currentValue={values.override_incorrect_value}
                    system={values.override_system}
                    attrCode={values.override_attrCode}
                    code={values.override_code}
                  />
                ) : (
                  <h4 style={{ marginTop: "1rem" }}>
                    <Note>Select applicable fields to view a summary.</Note>
                  </h4>
                )}
              </div>

              <div className={styles.bottomButtons}>
                <div className={styles.overrideCount}>
                  <span>
                    {values.override_action !== "" &&
                      values.override_attribute !== "" && (
                        <RecordOverrideCountString
                          selectedRecords={attributeValues}
                          attribute={values.override_attribute}
                          system={values.override_system}
                          attrCode={values.override_attrCode}
                          action={values.override_action}
                          incorrectValue={values.override_incorrect_value}
                          code={values.override_code}
                        />
                      )}
                  </span>
                </div>
                <Button
                  className={styles.button}
                  primary
                  type="submit"
                  disabled={(() => {
                    const records = getRecordsToOverride(
                      attributeValues,
                      values.override_attribute,
                      values.override_system,
                      values.override_attrCode,
                      values.override_action,
                      values.override_incorrect_value,
                      values.override_code,
                    );
                    return records?.length === 0 || records === undefined;
                  })()}
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => setIsOpen(false)}
                  className={styles.button}
                  cancel
                >
                  Cancel
                </Button>
                <div>
                  <div>
                    {errors.undefined && (
                      <span className={styles.errorText}>
                        {errors.undefined}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default GoldenRecordExclusionModal;
